import React from "react";
import {Skeleton, Box, Stack, Typography, Button, Chip, Divider, LinearProgress} from "@mui/material";
import { useApiManager } from "lib/ApiContext";
import { useParams } from "react-router-dom";
import { op } from "theme";
import EventHeader from "components/EventHeader";
import EventDetailsCard from "components/EventDetailsCard";
import NewsCard from "components/NewsCard";
import DescriptionCard from "components/DescriptionCard";
import ActivityRegistrationDialog from "components/ActivityRegistrationDialog";

const EventDetails = () => {
    const api = useApiManager();

    const [eventData, setEventData] = React.useState(null);
    const [partners, setPartners] = React.useState(null);
    const [activities, setActivities] = React.useState(null);
    const [loadingActivities, setLoadingActivities] = React.useState(true);
    
    const [noticias, setNoticias] = React.useState([]);
    const [loadingNoticias, setLoadingNoticias] = React.useState(true);
    
    // Estados para el diálogo de registro
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [registrationLoading, setRegistrationLoading] = React.useState(false);
    const [qrToken, setQrToken] = React.useState(null);
    const [registrationError, setRegistrationError] = React.useState(null);
    const [selectedActivity, setSelectedActivity] = React.useState(null);
   
    const { eventID } = useParams();

    React.useEffect(() => {
        loadEvent();
        loadNoticias();
    }, [eventID]);
   
    const loadEvent = async () => {
        if (!eventID) return;
        
        try {
            const eventData = await api.GetEventDetails(eventID);
            console.log("Datos del evento:", eventData);
            setEventData(eventData);
            
            try {
                const partnersData = await api.GetEventPartner(eventID);
                setPartners(partnersData.items || partnersData || []);
            } catch {
                setPartners([]);
            }
            
            try {
                const activitiesData = await api.GetEventActivities(eventID);
                
                if (Array.isArray(activitiesData)) {
                    setActivities(activitiesData);
                } else if (activitiesData?.items && Array.isArray(activitiesData.items)) {
                    setActivities(activitiesData.items);
                } else {
                    setActivities([]);
                }
            } catch {
                setActivities([]);
            }
        } catch (error) {
            console.error("Error loading event data:", error);
        } finally {
            setLoadingActivities(false);
        }
    };

    const loadNoticias = async () => {
        if (!eventID) return;
        
        setLoadingNoticias(true);
        try {
            const noticiasData = await api.GetEventNews(eventID);

            if (Array.isArray(noticiasData)) {
                setNoticias(noticiasData);
            } else if (noticiasData?.items) {
                setNoticias(noticiasData.items);
            } else if (noticiasData?.data) {
                setNoticias(noticiasData.data);
            } else {
                setNoticias([]);
            }
        } catch {
            setNoticias([]);
        } finally {
            setLoadingNoticias(false);
        }
    };

    const handleActivityClick = async (activity) => {
        console.log("🔵 Botón clickeado - Actividad:", activity.EventActivityID);
        
        // Guardar la actividad seleccionada
        setSelectedActivity(activity);
        console.log("🔵 Actividad guardada:", activity);
        
        // Resetear estados
        setQrToken(null);
        setRegistrationError(null);
        console.log("🔵 Estados reseteados");
        
        // Abrir el diálogo
        setDialogOpen(true);
        console.log("🔵 Diálogo abierto - dialogOpen:", true);
        
        // Iniciar el proceso de registro automáticamente
        setRegistrationLoading(true);
        console.log("🔵 Iniciando registro...");
        
        try {
            const response = await api.RegisterParticipant(activity.EventActivityID);
            
            console.log("✅ Respuesta del registro:", response);
            
            // Si la respuesta es null (CORS u otro error), lanzar excepción
            if (!response) {
                throw new Error("No se recibió respuesta del servidor. Verifica la configuración de CORS.");
            }
            
            // Extraer el token del QR de la respuesta
            if (response.QRToken) {
                setQrToken(response.QRToken);
            } else if (response.qrToken) {
                setQrToken(response.qrToken);
            } else if (response.token) {
                setQrToken(response.token);
            } else {
                // Si no hay QRToken explícito, usar el EventActivityID como fallback
                console.warn("⚠️ No se encontró QRToken en la respuesta, usando ID de actividad");
                setQrToken(activity.EventActivityID.toString());
            }
            
            // Recargar las actividades para actualizar el conteo
            loadEvent();
            
        } catch (error) {
            console.error("❌ Error al registrarse:", error);
            
            // Determinar el mensaje de error apropiado
            let errorMessage = "No se pudo completar el registro.";
            
            if (error.message && error.message.includes("CORS")) {
                errorMessage = "Error de conexión con el servidor. El backend necesita habilitar CORS para localhost:3000";
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = "No estás autenticado. Por favor, inicia sesión nuevamente.";
            } else if (error.response?.status === 400) {
                errorMessage = "Ya estás registrado en esta actividad o los datos son inválidos.";
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setRegistrationError(errorMessage);
        } finally {
            setRegistrationLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setQrToken(null);
        setRegistrationError(null);
        setSelectedActivity(null);
    };

    const getStatusLabel = (status) => {
        switch(status) {
            case 0: return 'Pendiente';
            case 1: return 'Aprobada';
            case 2: return 'Rechazada';
            default: return 'Desconocido';
        }
    };

    // v2: un solo nivel de superficie por columna (panel → filas planas)
    const panelSx = {
        backgroundColor: op.bg.surface,
        border: `1px solid ${op.border}`,
        borderRadius: '10px',
        p: 2.5,
    };

    return (
        <Stack spacing={2} direction="column">
            {eventData ? (
                <EventHeader eventdetails={eventData} />
            ) : (
                <Skeleton variant="rectangular" width="100%" height={220} />
            )}

            {/* LAYOUT 80/20: columna principal + sidebar, alturas naturales */}
            <Box sx={{
                px: 2,
                pb: 2,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 4fr) minmax(300px, 1fr)' },
                alignItems: 'start',
            }}>
                {/* COLUMNA PRINCIPAL (80%) — Actividades + Descripción */}
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                    <Box sx={panelSx}>
                        <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
                            Actividades
                        </Typography>

                        {loadingActivities ? (
                            <Stack spacing={1} sx={{ py: 1 }}>
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} variant="rounded" height={72} />
                                ))}
                            </Stack>
                        ) : activities && activities.length > 0 ? (
                            <Stack divider={<Divider />}>
                                {activities.map(act => {
                                    const capacity = act.Capacity || 0;
                                    const registered = act.RegisteredCount || 0;
                                    const fillPct = capacity > 0 ? Math.min(100, (registered / capacity) * 100) : 0;
                                    return (
                                        <Stack
                                            key={act.EventActivityID}
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={2}
                                            alignItems={{ xs: 'stretch', sm: 'center' }}
                                            justifyContent="space-between"
                                            sx={{ py: 2 }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                    <Typography variant="h4" component="h3" noWrap>
                                                        {act.ActivityName || 'Sin título'}
                                                    </Typography>
                                                    {act.Status !== 1 && (
                                                        <Chip
                                                            label={getStatusLabel(act.Status)}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'transparent',
                                                                color: act.Status === 2 ? 'error.main' : 'text.secondary',
                                                                border: `1px solid ${act.Status === 2 ? '#FF5C5C' : op.border}`,
                                                                fontSize: '0.7rem',
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                                                    {registered}/{capacity} inscritos
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={fillPct}
                                                    sx={{
                                                        height: 4,
                                                        borderRadius: 2,
                                                        maxWidth: 320,
                                                        backgroundColor: op.bg.elevated,
                                                        '& .MuiLinearProgress-bar': { backgroundColor: op.violet.main },
                                                    }}
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleActivityClick(act)}
                                                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                                            >
                                                Registrarme
                                            </Button>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Box
                                    component="img"
                                    src="/images/empty-activities.webp"
                                    alt=""
                                    sx={{ width: 160, maxWidth: '60%', borderRadius: '10px', mb: 2 }}
                                />
                                <Typography variant="h4" sx={{ color: 'text.secondary', mb: 1 }}>
                                    No hay actividades disponibles
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Este evento aún no tiene actividades registradas
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <DescriptionCard description={eventData?.EventDetails} />
                </Stack>

                {/* SIDEBAR (20%) — Detalles + Noticias */}
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                    {eventData ? (
                        <EventDetailsCard event={eventData} />
                    ) : (
                        <Skeleton variant="rounded" height={400} />
                    )}

                    <Box>
                        <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
                            Noticias
                        </Typography>

                        {loadingNoticias ? (
                            <Stack spacing={2}>
                                {[1, 2].map(i => (
                                    <Skeleton key={i} variant="rounded" height={200} />
                                ))}
                            </Stack>
                        ) : noticias && noticias.length > 0 ? (
                            <Stack spacing={2}>
                                {noticias.map((noticia) => (
                                    <NewsCard
                                        key={noticia.NewsID || noticia.newsID || noticia.id}
                                        icon={noticia.Icon || noticia.icon || '📰'}
                                        title={noticia.Title || noticia.titulo || 'Sin título'}
                                        description={noticia.Description || noticia.description || noticia.Content || noticia.content || ''}
                                        date={noticia.Date || noticia.date || noticia.CreatedAt || noticia.createdAt || ''}
                                        author={noticia.UserPublisher || noticia.author || noticia.CreatorName || noticia.creatorName || 'Organizador'}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ ...panelSx, textAlign: 'center', py: 4 }}>
                                <Box
                                    component="img"
                                    src="/images/empty-news.webp"
                                    alt=""
                                    sx={{ width: 120, maxWidth: '60%', borderRadius: '10px', mb: 2 }}
                                />
                                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    No hay noticias disponibles
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Aún no se han publicado noticias para este evento
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Box>

            {/* DIÁLOGO DE REGISTRO */}
            <ActivityRegistrationDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                loading={registrationLoading}
                qrToken={qrToken}
                error={registrationError}
                activityName={selectedActivity?.ActivityName}
            />
        </Stack>
    );
};

export default EventDetails;